import React from 'react';
import { useTranslation } from 'react-i18next';
import API from '@aws-amplify/api';
import { ShipyardContext } from '../../../Context';
import { getReducer } from './reducer';

const initialState = {
    filterString: '',
    page: 0,
    rowsPerPage: 25,
    searchTerm: '',
    filters: [],
    term: 10, //TODO TechDebt - This is hardcoded but should come from the database somehow, can't be done with 2.2.1 due to crunch.
    listData: [],
    isFetching: false,
    isInitialized: false,
    tableHeaders: [],
    hasMore: false,
    currentCount: 0,
    totalRecordCount: 0,
    sortDirection: 'Asc',
    recOffset: 0,
    useCache: false,
    statusRef: {},
    selectedItem: new Set(),
    allChecked: false,
};

const useFilterBuilder = (startingField, ctx) => {
    //utility hooks
    const [t] = useTranslation();
    const { listSettings, setListSettings } = React.useContext(ShipyardContext);
    const ctxSafeReducer = ctx !== listSettings.target || !listSettings.useCache ? { filterString: '' } : listSettings.reducerState;
    const freshInitialState = {
        ...initialState,
        sortString: `${startingField} Asc`,
        sortField: `${startingField}`,
        ...ctxSafeReducer,
    };

    const [state, dispatch] = React.useReducer(getReducer(ctx), freshInitialState);

    const apiParams = {
        filterString: state.filterString,
        sortString: state.sortString,
        recOffset: state.recOffset,
        term: state.term,
    };

    const handleSearchTerm = (value) => {
        if(value !== state?.searchTerm) {
            dispatch({ type: 'toggleIsFetching', payload: true });
            dispatch({
                type: 'search',
                payload: {
                    searchTerm: value,
                },
            });
        }
    };

    const handleSort = (field) => {
        dispatch({
            type: 'sort',
            payload: {
                field,
            },
        });
    };

    const handleSelectTerm = (periodTerm) => {
        dispatch({
            type: 'changeTermYear',
            payload: {
                term: periodTerm,
            },
        });
    };

    const handleFilterChange = (e, label) => {
        dispatch({
            type: 'changeFilters',
            payload: {
                value: e?.target?.value || [],
                label,
            },
        });
    };

    const handleClearFilters = () => {
        dispatch({
            type: 'clearFilters',
            payload: {
                filters: state.filters.map((filter) => ({ ...filter, value: [] })),
            },
        });
    };

    const getInvoices = async (apiFn) => {
        if (listSettings.useCache) {
            if (listSettings.target !== ctx) {
                dispatch({
                    type: 'patch',
                    payload: {
                        filterString: '',
                        sortString: `${startingField} Asc`,
                        sortField: `${startingField}`,
                    },
                });
            }
            setListSettings({ ...listSettings, useCache: false });
        }

        dispatch({ type: 'toggleIsFetching', payload: true });

        try {
            const { data, headers } = await apiFn(apiParams, (dataHeaders) =>
                dataHeaders.map((each) => ({ ...each, label: t(each.label), handleSort }))
            );
            const totalRecordCount = data[0]?.TotalRecordCount || 0;
            dispatch({
                type: 'setListWithHeaders',
                payload: {
                    hasMore: data.length < totalRecordCount,
                    currentCount: totalRecordCount,
                    totalRecordCount,
                    listData: data,
                    tableHeaders: headers,
                    useCache: false,
                    selectedItem: new Set(),
                    isFetching: false,
                },
            });
        } catch (e) {
            dispatch({ type: 'toggleIsFetching', payload: false });
        } finally {
            setListSettings({
                target: null,
                reducerState: null,
                useCache: false,
            });
        }
    };

    const loadMore = async (apiFn) => {
        dispatch({ type: 'toggleIsFetching', payload: true });
        try {
            const { data } = await apiFn(apiParams, () => {});
            setListSettings({ ...listSettings, useCache: false });
            dispatch({
                type: 'setListWithoutHeaders',
                payload: {
                    listData: data,
                },
            });
        } catch (e) {
            dispatch({ type: 'toggleIsFetching', payload: false });
        }
    };

    const handleCheckbox = (e, listItem) => {
        var newSelectedItem = new Set(state?.selectedItem);
        if(e?.target?.checked) {
            newSelectedItem.add(listItem);
        } else {
            newSelectedItem.delete(listItem);
        }

        dispatch({
            type: 'patch',
            payload: {
                selectedItem: newSelectedItem,
                allChecked: newSelectedItem?.size === state?.listData?.length,
            }
        });
    };

    const handleCheckAll = (e) => {
        let _newSet;
        if(e?.target?.checked) {
            _newSet = new Set(state?.listData);
        } else {
            _newSet = new Set();
        };

        dispatch({
            type: 'patch',
            payload: {
                selectedItem: _newSet,
                allChecked: e?.target?.checked === true,
            }
        });
    };

    const getFilters = async (filterRoute, setStatuses) => {
        if (listSettings.useCache && listSettings.target === ctx) {
            return;
        }

        const res = await API.get('ShipyardAPI', filterRoute);
        const statuses = ctx === 'invoices' ? res['StatusID'] : res['Status'];
        let differentStatuses;
        if (ctx === 'invoices') {
            differentStatuses = statuses?.labels?.map((label, i) => ({ label, hex: statuses?.hexColors[i] }));
        } else {
            differentStatuses = statuses.map((a) => ({ id: a.id, label: a.description, hex: a.displayColor }));
        }
        const obj = differentStatuses?.reduce((prev, cur, i) => {
            return { ...prev, [`${t(cur?.label)}`]: cur };
        }, {});

        dispatch({
            type: 'setStatuses',
            payload: {
                statusRef: obj,
            },
        });

        const newFilters = Object.keys(res).map((key) => {
            if (key === 'GradeID') {
                return {
                    label: 'Grade',
                    id: 'Grade',
                    value: [],
                    handleChange: handleFilterChange,
                    options: res['GradeID'].map((each) => ({ label: t(each.Description), value: t(each.Description) })),
                };
            }
            if (key === 'StatusId' || key === 'StatusID') {
                return {
                    label: 'Status',
                    id: key === 'StatusID' ? 'InvoiceStatus' : key,
                    value: [],
                    handleChange: handleFilterChange,
                    options: res[key].labels.map((label) => ({ label: t(label), value: t(label) })),
                };
            }
            if (key === 'Status') {
                return {
                    label: 'Status',
                    id: 'StatusID',
                    value: [],
                    handleChange: handleFilterChange,
                    options: res['Status'].map((a) => ({ label: a.description, value: a.id })),
                };
            }
            if (key === 'Periods') {
                return {
                    label: 'Period',
                    id: 'Period',
                    value: [],
                    handleChange: handleFilterChange,
                    options: res[key].map((period) => ({ label: period.PeriodDescription, value: period.PeriodDescription })),
                };
            }
            if (typeof res[key][0] === 'string') {
                return {
                    label: t(`Filters.${key}`),
                    id: key,
                    value: [],
                    handleChange: handleFilterChange,
                    options: res[key].map((label) => ({ label: t(label), value: t(label) })),
                };
            }
            if (key === 'TermID') {
                return {
                    label: t(`Filters.${key}`),
                    id: key,
                    value: [],
                    handleChange: handleFilterChange,
                    options: res[key].map((obj) => ({ label: t(obj.label), value: obj.value })),
                };
            }
            const newObj = {
                label: t(`Filters.${key}`),
                id: key,
                value: [],
                handleChange: handleFilterChange,
                options: res[key],
            };

            return newObj;
        });
        dispatch({
            type: 'setFilters',
            payload: { filters: newFilters },
        });
    };

    const handlePageChange = (event, newPage) => {
        dispatch({
            type: 'setPage',
            payload: {
                newPage,
            }
        });
    };

    const handleRowsPerPageChange = (event) => {
        dispatch({
            type: 'setRowsPerPage',
            payload: {
                newPage: 0,
                newRowsPerPage: event.target.value
            }
        });
    };

    // USE THIS CODE TO DEBUG CHANGES - PLEASE DO NOT DELETE
    // React.useEffect(() => {
    //   console.log("something in state has changed", state)
    // }, [state.listData, state.isFetching, state.filterString])

    return {
        ...state,
        filterString: state.filterString,
        handleSearchTerm,
        handleSort,
        handleSelectTerm,
        handleFilterChange,
        handleClearFilters,
        getInvoices,
        getFilters,
        loadMore,
        handlePageChange,
        handleRowsPerPageChange,
        handleCheckbox,
        handleCheckAll,
        ctx,
    };
};

export default useFilterBuilder;

